//library imports
import { Suspense, useEffect, useLayoutEffect, useState } from "react";
import { Switch, Route, Redirect, useParams, useLocation } from "react-router-dom";
import { refreshToken } from "./actions/auth";
import { loadAllFeatures } from "./actions/features";
import { useDispatch } from "react-redux";
import { lazy } from "react";
import { withRouter } from "react-router-dom";
import { initializeApp } from "firebase/app";

//Routes
const PrivateRoute = lazy(() => import("./components/PrivateRoute"));
const TherapistPrivateRoute = lazy(() => import("./components/TherapistPrivateRoute"));
const AdminPrivateRoute = lazy(() => import("./components/AdminPrivateRoute"));
const AdminDashboardShell = lazy(() => import("./components/AdminDashboard"));

//utilities
import Loading from "./components/Utilities/Loading/Loading";

//screens
const AboutUs = lazy(() => import("./screens/AboutUs.js"));
const ForgotPasswordScreen = lazy(() => import("./screens/ForgotPasswordScreen.js"));
const DashboardScreen = lazy(() => import("./screens/afterLoginScreen/DashboardScreen.js"));
const MyTherapistScreen = lazy(() => import("./screens/admin/MyTherapistScreen.js"));
const MyClientScreen = lazy(() => import("./screens/admin/MyClientScreen.js"));
const SingleClientScreen = lazy(() => import("./screens/admin/SingleClientScreen.js"));
const OnBoardNewClient = lazy(() => import("./screens/admin/OnBoardNewClient"));
const OnBoardNewTherapist = lazy(() => import("./screens/admin/OnBoardNewTherapist"));
const SingleTherapistScreen = lazy(() => import("./screens/admin/SingleTherapistScreen"));
const TherapistUpdateScreen = lazy(() => import("./screens/admin/TherapistUpdateScreen"));
const ClientUpdateScreen = lazy(() => import("./screens/admin/ClientUpdateScreen"));
const AllQuizzesScreen = lazy(() => import("./screens/quiz/AllQuizzesScreen"));
const SingleQuizScreen = lazy(() => import("./screens/quiz/SingleQuizScreen"));
const PaymentRoute = lazy(() => import("./screens/afterLoginScreen/PaymentRoute"));
const ViewSessionLevelMonth = lazy(() => import("./screens/admin/ViewSessionLevelMonth"));
const ViewSessionLevelTherapist = lazy(() => import("./screens/admin/ViewSessionLevelTherapist"));
const UserAnalyticsScreen = lazy(() => import("./screens/admin/UserAnalyticsScreen"));
const FeedbackInfo = lazy(() => import("./screens/admin/FeedbackInfo"));
const PageNotFound = lazy(() => import("./screens/PageNotFound/PageNotFound.js"));
const LoginPageCheck = lazy(() => import("./Containers/Login"));
const RegistrationPageCheck = lazy(() => import("./Containers/Registration"));
const AssessmentQuiz = lazy(() => import("./Containers/Assessment Quiz/index.js"));
const EmployeeWellness = lazy(() => import("./screens/landing-pages/employeeWellness/EmployeeWellness.js"));
const PaymentPlan = lazy(() => import("./Containers/Payment Plan/index.js"));
const GuestMarketplacePackages = lazy(() => import("./Containers/Marketplace/GuestMarketplacePackages"));
const GuestMarketplaceSlotScreen = lazy(() => import("./Containers/Marketplace/GuestMarketplaceSlotScreen"));
const SessionMeeting = lazy(() => import("./components/SessionMeeting"));
const WorkshopMeeting = lazy(() => import("./components/WorkshopMeeting"));
const VideoScreen = lazy(() => import("./screens/VideoPlayer/videoPlayer"));
const UserOnboardingQuestions = lazy(() => import("./Containers/UserOnboardingQuestions/UserOnboardingQuestions"));
const Meeting = lazy(() => import("components/Meeting/index"));
const MeetingControls = lazy(() => import("components/MeetingControls/MeetingControls"));

//css before
//import "./App.css"
import "bootstrap/dist/css/bootstrap.min.css";
import "components/index.css";
import { logoutUser, setAvailableFeatures, setFeatureFlags, setUserInfo, setUserToken } from "store/actions";
import {
  ANALYTICS,
  CHALLENGES,
  LIBRARY,
  MOOD_TRACKER,
  MY_THERAPIST,
  MY_BOOKINGS,
  MY_PROFILE,
  OVERVIEW,
  SUPPORT,
  WALLET,
  EVENTS,
  REDEEM_COINS,
  ASSESSMENTS,
  THERAPY_BOOKING,
  ASSESSMENT_QUIZ,
  ASSESSMENT_RESULTS,
} from "components/UserDashboard";
import {
  HR_ASSESSMENT_SCORE,
  HR_OVERVIEW,
  HR_SESSIONS,
  HR_SUPPORT,
  HR_USERS,
  HR_ZEN_ANALYTICS,
  HR_EVENTS,
  HR_THERAPY,
  HR_CHALLENGES,
} from "components/HRDashboard";
import OTPVerification from "Containers/OTPVerification";
import EmailLogin from "Containers/EmailLogin";
import ForgotPassword from "Containers/ForgetPassword";
import BuildYourSpace from "Containers/BuildYourSpace";
import TherapistDashboard, {
  THERAPIST_CASENOTES,
  THERAPIST_CHATS,
  THERAPIST_CLIENT_PROFILE,
  THERAPIST_EVENTS,
  THERAPIST_MY_CLIENTS,
  THERAPIST_MY_PROFILE,
  THERAPIST_MY_SESSIONS,
  THERAPIST_TASKS,
} from "components/Dashboard/TherapistDashboard";
import firebaseConfig from "Config/firebaseConfig";
import DemoPage from "screens/landing-pages/demoPage";
import { useIsFeatureEnabled } from "store/reducers";
import TherapistLogin from "Containers/TherapistLogin";

const BookingSelection = lazy(() => import("./Containers/Booking Selection/index.js"));
const RescheduleSession = lazy(() => import("./Containers/Reschedule/index.js"));
const Home = lazy(() => import("ContainersUpdated/Home"));
const HomeB2CAds = lazy(() => import("ContainersUpdated/HomeB2CAds"));
const PulseCheckIn = lazy(() => import("./ContainersUpdated/Pulse Check-in"));
const EmployeeEngagement = lazy(() => import("./ContainersUpdated/Employee Engagement"));
const PredictBurnout = lazy(() => import("./ContainersUpdated/Predict Burnout"));
const OverallWellness = lazy(() => import("./ContainersUpdated/Overall wellness"));
const CorporateMentalHealthPrograms = lazy(
  () => import("screens/landing-pages/corporateMentalHealthPrograms/corporateMentalHealthPrograms"),
);
const EmployeeMentalHealth = lazy(() => import("screens/landing-pages/employeeMentalHealth/employeeMentalHealth"));
const CorporateWellness = lazy(() => import("screens/landing-pages/corporateWellness/corporateWellness"));
const CommunityPopup = lazy(() => import("components/CommunityPopup/CommunityPopup"));
const SlackClientScreen = lazy(() => import("screens/admin/SlackClientScreen"));
const AdminHRDashboard = lazy(() => import("screens/admin/AdminHRDashboard"));

// Therapist Screens

export const firebaseApp = initializeApp(firebaseConfig);

const App = () => {
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();
  const isGuestBookingEnabled = useIsFeatureEnabled("guest-session-booking", "public");
  const isTherapistBookingEnabled = useIsFeatureEnabled("therapist-onboarding", "public");
  // public "marketplace" flag: the therapist listing for logged out visitors.
  // The private copy of the same key drives the marketplace inside the dashboard.
  const isPublicMarketplaceEnabled = useIsFeatureEnabled("marketplace", "public");

  //runs only on initial render
  useEffect(() => {
    refreshTheExistToken();
    // eslint-disable-next-line
    const routesToShowPopup = ["/", "/overall-wellness", "/employee-engagement", "/pulse-check-in", "/predict-burnout"];

    const popupShown = sessionStorage.getItem("popupShown");

    if (!popupShown && routesToShowPopup.includes(location.pathname)) {
      const timeout = setTimeout(() => {
        setShowPopup(true);
        sessionStorage.setItem("popupShown", "true");
      }, 15000);
      return () => clearTimeout(timeout);
    }
  }, []);

  const handleContinue = () => {
    setShowPopup(false);
  };

  const handleJoin = () => {
    setShowPopup(false);
    window.open("https://docs.google.com/forms/d/e/1FAIpQLSdpxs63Wn9o_Hjy3NAqhwCFU_WLCAajV6RvrSzBt09FjEzpLQ/viewform", "_blank");
  };

  //gets the logged in user access by renewing access token
  const refreshTheExistToken = async () => {
    try {
      const res = await refreshToken();
      const { userToken, userInfo, featureFlags } = res.data;
      window.localStorage.setItem("auth", JSON.stringify(res.data));
      dispatch(setUserToken(userToken));
      dispatch(setUserInfo(userInfo));
      dispatch(setFeatureFlags(featureFlags, "public"));

      const allFeatures = await loadAllFeatures(userToken);
      dispatch(setAvailableFeatures(allFeatures.data));

      return setLoading(false);
    } catch (e) {
      dispatch(setFeatureFlags(e?.response?.data?.featureFlags || [], "public"));
      logoutUser(dispatch);
      window.localStorage.removeItem("auth");
      setLoading(false);
    }
  };
  // refreshTheExistToken();
  // useLayoutEffect(() => refreshTheExistToken(), [])

  function GetURLId() {
    let { id } = useParams();
    window.location.href = "http://goodlives.in/.well-known/acme-challenge/" + id;
    return null;
  }

  function CorporatePageRedirect() {
    window.location.href = "/overall-wellness";
    return null;
  }

  return (
    <>
      {/* <Navbar /> */}
      {loading ? (
        <Loading show={true} />
      ) : (
        <Suspense fallback={<Loading show={true} />}>
          <Loading />
          <Switch>
            {/* public & auth routes */}
            <Route path="/" exact component={Home} />
            <Route exact path="/.well-known/acme-challenge/:id" children={<GetURLId />} />
            <Route exact path="/corporates" children={<CorporatePageRedirect />} />
            <Route path="/employee-wellness" exact component={EmployeeWellness} />
            <Route path="/corporate-wellness" exact component={CorporateWellness} />
            <Route path="/learning-and-development" exact component={DemoPage} />
            <Route path="/corporate-mental-health-programs" exact component={CorporateMentalHealthPrograms} />
            <Route path="/employee-mental-health" exact component={EmployeeMentalHealth} />
            <Route path="/about-us" exact component={AboutUs} />
            <Route path="/pulse-check-in" component={PulseCheckIn} />
            <Route path="/employee-engagement" component={EmployeeEngagement} />
            <Route path="/predict-burnout" component={PredictBurnout} />
            <Route path="/overall-wellness" component={OverallWellness} />
            <Route path="/signin" exact component={LoginPageCheck} />
            {isTherapistBookingEnabled && (<Route path="/therapist/signup" exact component={TherapistLogin} />)}
            <Route exact path="/signup" component={RegistrationPageCheck} />
            <Route path="/forgotpassword" exact component={ForgotPasswordScreen} />
            <Route path="/otpVerification" exact component={OTPVerification} />
            <Route path="/login" exact component={EmailLogin} />
            <Route path="/forgetPassword" exact component={ForgotPassword} />
            <Route path="/buildYourSpace" exact component={BuildYourSpace} />
            <Route path="/controls" exact component={MeetingControls} />
            <Route path="/controls/:meetingNumber/:passWord" exact component={Meeting} />
            {isPublicMarketplaceEnabled && <Route path="/listing" exact component={HomeB2CAds} />}
            {isPublicMarketplaceEnabled && isGuestBookingEnabled && (
              <Route path="/listing/:therapistId/packages" exact component={GuestMarketplacePackages} />
            )}
            {isPublicMarketplaceEnabled && isGuestBookingEnabled && (
              <Route path="/listing/:therapistId/slots" exact component={GuestMarketplaceSlotScreen} />
            )}
            {!isPublicMarketplaceEnabled && <Redirect from="/listing" to="/" />}

            {/* B2B & B2c user routes */}
            <PrivateRoute path="/onboardingQuestions" exact component={UserOnboardingQuestions} />
            {<PrivateRoute path="/quiz-old" exact component={AllQuizzesScreen} />}
            <PrivateRoute path="/quiz/:quizNo" exact component={AssessmentQuiz} />
            <PrivateRoute path="/quiz-old/:quizNo" exact component={SingleQuizScreen} />
            <PrivateRoute path="/session/:meetid/" exact component={SessionMeeting} />
            <PrivateRoute path="/workshop/:workshopId/" exact component={WorkshopMeeting} />
            <PrivateRoute path="/workshops/:workshopId" exact component={DashboardScreen} />
            <PrivateRoute path="/rescheduleSession" exact feature="Therapy Booking" component={RescheduleSession} />
            <PrivateRoute path="/bookingDetails" exact component={PaymentRoute} />
            <PrivateRoute
              path="/sessionBooking/payment-plans"
              exact
              render={(props) => <PaymentPlan {...props} routeProps={{ categoryId: 1, currency: "INR" }} />}
            />
            <PrivateRoute
              path="/sessionBooking/payment-failure"
              exact
              render={(props) => <PaymentPlan {...props} routeProps={{ errorPayment: true }} />}
            />
            <PrivateRoute
              path="/dashboard"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: "" }} />}
            />
            <PrivateRoute
              path="/dashboard/overview"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: OVERVIEW }} />}
            />
            <PrivateRoute
              path="/dashboard/sessionBooking"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: THERAPY_BOOKING }} />}
            />
            <PrivateRoute
              path="/dashboard/library"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: LIBRARY }} />}
            />
            <PrivateRoute path="/dashboard/library/video" render={(props) => <VideoScreen {...props} />} />
            <PrivateRoute
              path="/dashboard/assessments"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: ASSESSMENTS }} />}
            />
            <PrivateRoute
              path="/dashboard/assessments/view/:assessmentId"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: ASSESSMENTS }} />}
            />
            <PrivateRoute
              path="/dashboard/assessments/results/:urlSlug"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: ASSESSMENT_RESULTS }} />}
            />
            <PrivateRoute
              path="/dashboard/assessments/:urlSlug"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: ASSESSMENT_QUIZ }} />}
            />

            <PrivateRoute
              path="/dashboard/mood-tracker"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: MOOD_TRACKER }} />}
            />
            <PrivateRoute
              path="/dashboard/my-bookings"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: MY_BOOKINGS }} />}
            />
            <PrivateRoute
              path="/dashboard/my-therapist"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: MY_THERAPIST }} />}
            />
            <PrivateRoute
              path="/dashboard/events"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: EVENTS }} />}
            />
            <PrivateRoute
              path="/dashboard/wallet"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: WALLET }} />}
            />
            <PrivateRoute
              path="/dashboard/profile"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: MY_PROFILE }} />}
            />
            <PrivateRoute
              path="/dashboard/analytics"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: ANALYTICS }} />}
            />
            <PrivateRoute
              path="/dashboard/challenges"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: CHALLENGES }} />}
            />
            <PrivateRoute
              path="/dashboard/redeem"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: REDEEM_COINS }} />}
            />
            <PrivateRoute
              path="/dashboard/support"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: SUPPORT }} />}
            />

            {/* Therapist routes */}
            <TherapistPrivateRoute
              path="/dashboard/therapist/sessions"
              exact
              render={(props) => <TherapistDashboard {...props} routeProps={{ activeTab: THERAPIST_MY_SESSIONS }} />}
            />
            <TherapistPrivateRoute
              path="/dashboard/therapist/chats"
              exact
              render={(props) => <TherapistDashboard {...props} routeProps={{ activeTab: THERAPIST_CHATS }} />}
            />
            <TherapistPrivateRoute
              path="/dashboard/therapist/chats/:clientId"
              exact
              render={(props) => <TherapistDashboard {...props} routeProps={{ activeTab: THERAPIST_CHATS }} />}
            />
            <TherapistPrivateRoute
              path="/dashboard/therapist/clients"
              exact
              render={(props) => <TherapistDashboard {...props} routeProps={{ activeTab: THERAPIST_MY_CLIENTS }} />}
            />
            <TherapistPrivateRoute
              path="/dashboard/therapist/clients/:clientId"
              exact
              render={(props) => <TherapistDashboard {...props} routeProps={{ activeTab: THERAPIST_CLIENT_PROFILE }} />}
            />
            <TherapistPrivateRoute
              path="/dashboard/therapist/events"
              exact
              render={(props) => <TherapistDashboard {...props} routeProps={{ activeTab: THERAPIST_EVENTS }} />}
            />
            <TherapistPrivateRoute
              path="/dashboard/therapist/profile"
              exact
              render={(props) => <TherapistDashboard {...props} routeProps={{ activeTab: THERAPIST_MY_PROFILE }} />}
            />
            <TherapistPrivateRoute
              path="/dashboard/therapist/casenotes"
              exact
              render={(props) => <TherapistDashboard {...props} routeProps={{ activeTab: THERAPIST_CASENOTES }} />}
            />
            <TherapistPrivateRoute
              path="/dashboard/therapist/tasks"
              exact
              render={(props) => <TherapistDashboard {...props} routeProps={{ activeTab: THERAPIST_TASKS }} />}
            />

            <TherapistPrivateRoute
              path="/dashboard/therapist/rescheduleSession"
              exact
              render={(props) => <RescheduleSession {...props} routeProps={{ activeTab: THERAPIST_MY_SESSIONS }} />}
            />

            {/* HR routes */}
            <PrivateRoute
              path="/hr/dashboard/overview"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: HR_OVERVIEW }} />}
            />
            <PrivateRoute
              path="/hr/dashboard/users"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: HR_USERS }} />}
            />
            <PrivateRoute
              path="/hr/dashboard/sessions"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: HR_SESSIONS }} />}
            />
            <PrivateRoute
              path="/hr/dashboard/slack"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: HR_ZEN_ANALYTICS }} />}
            />
            <PrivateRoute
              path="/hr/dashboard/assessment-score"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: HR_ASSESSMENT_SCORE }} />}
            />
            <PrivateRoute
              path="/hr/dashboard/support"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: HR_SUPPORT }} />}
            />
            <PrivateRoute
              path="/hr/dashboard/events"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: HR_EVENTS }} />}
            />
            <PrivateRoute
              path="/hr/dashboard/therapy"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: HR_THERAPY }} />}
            />
            <PrivateRoute
              path="/hr/dashboard/challenges"
              exact
              render={(props) => <DashboardScreen {...props} routeProps={{ activeTab: HR_CHALLENGES }} />}
            />

            {/* admin routes  */}
            {/* Every admin screen lives under /admin/:tab, rendered inside the
                dashboard shell so the sidebar is always present and the URL
                always reflects what is on screen. */}
            <AdminPrivateRoute path="/admin/:tab" component={AdminDashboardShell} />
            <AdminPrivateRoute path="/admin" exact component={AdminDashboardShell} />
            <AdminPrivateRoute path="/ViewSessionLevelMonth" exact component={ViewSessionLevelMonth} />
            <AdminPrivateRoute path="/ViewSessionLevelTherapist" exact component={ViewSessionLevelTherapist} />
            <AdminPrivateRoute path="/feedbackInfo" exact component={FeedbackInfo} />
            <AdminPrivateRoute path="/userAnalytics" exact component={UserAnalyticsScreen} />
            <AdminPrivateRoute path="/mytherapists" exact component={MyTherapistScreen} />
            <AdminPrivateRoute path="/mytherapists/:therapistId" exact component={SingleTherapistScreen} />
            <AdminPrivateRoute path="/mytherapists/update/:therapistId" exact component={TherapistUpdateScreen} />
            <AdminPrivateRoute path="/myclients" exact component={MyClientScreen} />
            <AdminPrivateRoute path="/myclients/:clientId" exact component={SingleClientScreen} />
            <AdminPrivateRoute path="/myclients/update/:clientId" exact component={ClientUpdateScreen} />
            <AdminPrivateRoute path="/myclients/dashboard/:clientId" exact component={AdminHRDashboard} />
            <AdminPrivateRoute path="/onboardnewclient" exact component={OnBoardNewClient} />
            <AdminPrivateRoute path="/onboardnewtherapist" exact component={OnBoardNewTherapist} />
            <AdminPrivateRoute path="/slackClients/:clientId" exact component={SlackClientScreen} />
            <Route component={PageNotFound} />
          </Switch>
          <CommunityPopup isOpen={showPopup} onClose={handleContinue} onJoin={handleJoin} />
        </Suspense>
      )}
    </>
  );
};

export default withRouter(App);
